// src/components/E2E/SubmissionReview.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import AttachmentListSection from "./AttachmentListSection";
import SubmissionSectionTreeWithComments from "./SubmissionSectionTreeWithComments";
import SectionNavigator from "./SectionNavigator";
import { IDSButton, IDSTextarea, IDSBreadcrumbs, IDSAlert, IDSCheckbox, IDSSelect } from "@inera/ids-react";
import { formatDateTimeNoSeconds } from "../../utils/dateTime";
import { getSectionCommentSummaries, filterSectionsWithComments } from "../../utils/e2eSectionComments";
import {
  approveReviewerSubmission,
  assignReviewerSubmissionToMe,
  getReviewerSubmission,
  getSubmissionComments,
  rejectReviewerSubmission,
  requestClarification,
  unassignReviewerSubmission,
  getAssignableReviewers,
  assignReviewerSubmissionTo,
  unassignReviewerFromSubmission,
  getAttachments,
  downloadAttachment,
} from '../../services/E2EAPI';
import { getSubmissionStatusLabel } from "../../utils/submissionStatus";
import {
  fetchE2EUserProfilesByIds,
  getE2EUserProfileDetails as getUserProfileDetails,
  resolveE2EUserDisplayName,
} from "../../utils/e2eUserProfiles";

/* ─────────────────────────────────────────────────────────────
   Main Component
───────────────────────────────────────────────────────────── */

export default function SubmissionReview() {
  const { submissionId: id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  const [submission, setSubmission] = useState(null);
  const [allComments, setAllComments] = useState([]);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [actionError, setActionError] = useState("");
  const [commentError, setCommentError] = useState("");
  const [showOnlyWithComments, setShowOnlyWithComments] = useState(false);

  const [clarifyLoading, setClarifyLoading] = useState(false);
  const [clarifyError, setClarifyError] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignableReviewers, setAssignableReviewers] = useState([]);
  const [selectedReviewerId, setSelectedReviewerId] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [userProfilesById, setUserProfilesById] = useState({});

  // Sections actually rendered after applying the "only commented" filter
  const visibleSections = useMemo(() => {
    const all = submission?.form?.sections || [];
    return showOnlyWithComments ? filterSectionsWithComments(all, allComments) : all;
  }, [submission, allComments, showOnlyWithComments]);

  // Per-section comment counts for the section navigator (follows the filter)
  const sectionStatus = useMemo(
    () => getSectionCommentSummaries(visibleSections, allComments),
    [visibleSections, allComments]
  );

  // Re-read comments after one is added or removed so counts and filters follow along
  const refreshComments = useCallback(async () => {
    try {
      const commentsData = await getSubmissionComments(id, true);
      setAllComments(commentsData || []);
    } catch (err) {
      console.error('Failed to refresh comments:', err);
    }
  }, [id]);

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        setLoadError("");

        const data = await getReviewerSubmission(id);
        setSubmission(data);

        try {
          const relatedUserIds = [
            data?.userId,
            ...(Array.isArray(data?.reviewedBy) ? data.reviewedBy : []),
            user?.id,
            user?.sub,
          ];
          const profiles = await fetchE2EUserProfilesByIds(relatedUserIds);
          setUserProfilesById(profiles);
        } catch (profileErr) {
          console.error('Failed to load user profiles:', profileErr);
        }

        try {
          const commentsData = await getSubmissionComments(id, true);
          setAllComments(commentsData || []);
        } catch (commentsErr) {
          console.error('Failed to load comments:', commentsErr);
        }

        try {
          const attachmentsData = await getAttachments(id, true);
          setAttachments(attachmentsData || []);
        } catch (attErr) {
          console.error('Failed to load attachments:', attErr);
        }

        // Colleagues to hand the submission over to. Reviewers without a
        // department get an empty list and only see "Tilldela mig".
        try {
          const reviewers = await getAssignableReviewers(id);
          setAssignableReviewers(reviewers);
        } catch (reviewersErr) {
          console.error('Failed to load assignable reviewers:', reviewersErr);
          setAssignableReviewers([]);
        }

      } catch (err) {
        console.error("Failed to load submission", err);
        setLoadError("Kunde inte ladda inlämningen. Försök ladda om sidan eller kontakta support.");
      } finally {
        setLoading(false);
      }
    }

    if (id && isAuthenticated) fetchData();
  }, [id, isAuthenticated, user?.id, user?.sub]);


  async function handleAssignToMe() {
    try {
      setAssignLoading(true);
      setActionError("");
      const updatedSubmission = await assignReviewerSubmissionToMe(id);
      setSubmission((prev) => updatedSubmission || {
        ...prev,
        reviewedBy: [...(prev.reviewedBy || []), user?.id || user?.email || user?.sub || "Du"],
      });
    } catch (err) {
      console.error("Failed to assign submission", err);
      setActionError("Kunde inte tilldela inlämningen. Försök igen.");
    } finally {
      setAssignLoading(false);
    }
  }
  
  async function handleUnassign() {
    try {
      setAssignLoading(true);
      setActionError("");
      const updatedSubmission = await unassignReviewerSubmission(id);
      setSubmission((prev) => updatedSubmission || {
        ...prev,
        reviewedBy: (prev.reviewedBy || []).filter((r) => r !== user?.id),
      });
    } catch (err) {
      console.error("Failed to unassign submission", err);
      setActionError("Kunde inte ta bort tilldelningen. Försök igen.");
    } finally {
      setAssignLoading(false);
    }
  }

  async function handleAssignToColleague() {
    if (!selectedReviewerId) return;

    try {
      setAssignLoading(true);
      setActionError("");
      const updatedSubmission = await assignReviewerSubmissionTo(id, selectedReviewerId);
      setSubmission((prev) => updatedSubmission || {
        ...prev,
        reviewedBy: [...(prev.reviewedBy || []), selectedReviewerId],
      });
      setSelectedReviewerId("");
    } catch (err) {
      console.error("Failed to assign submission to colleague", err);
      setActionError(err.message || "Kunde inte tilldela granskaren. Försök igen.");
    } finally {
      setAssignLoading(false);
    }
  }

  async function handleUnassignReviewer(reviewerId) {
    try {
      setAssignLoading(true);
      setActionError("");
      const updatedSubmission = await unassignReviewerFromSubmission(id, reviewerId);
      setSubmission((prev) => updatedSubmission || {
        ...prev,
        reviewedBy: (prev.reviewedBy || []).filter((r) => r !== reviewerId),
      });
    } catch (err) {
      console.error("Failed to unassign reviewer", err);
      setActionError(err.message || "Kunde inte ta bort tilldelningen. Försök igen.");
    } finally {
      setAssignLoading(false);
    }
  }
  async function handleDecision(type) {
    if (!comment.trim()) {
      setCommentError("Ange en kommentar innan du skickar ditt beslut.");
      return;
    }
    setCommentError("");
    try {
      if (type === "approve") {
        await approveReviewerSubmission(id, comment);
      } else {
        await rejectReviewerSubmission(id, comment);
      }
      navigate("/e2e/reviewer/submissions");
    } catch (err) {
      console.error("Failed to submit decision", err);
      setActionError("Kunde inte skicka beslutet. Försök igen.");
    }
  }

  async function handleRequestClarification() {
    if (!comment.trim()) {
      setCommentError("Ange en kommentar som förklarar vad testaren behöver förtydliga.");
      return;
    }
    setCommentError("");
    try {
      setClarifyLoading(true);
      setClarifyError('');
      await requestClarification(id, null, comment, true);
      navigate('/e2e/reviewer/submissions');
    } catch (err) {
      console.error('Failed to request clarification', err);
      setClarifyError('Kunde inte begära förtydligande. Försök igen.');
    } finally {
      setClarifyLoading(false);
    }
  }

  if (loading) return <p className="ids-mt-4">Laddar inlämning…</p>;
  if (loadError) return (
    <div className="ids-m-6">
      <main className="ids-content ids-p-6" style={{ maxWidth: "1200px", margin: "2rem auto" }}>
        <IDSAlert type="error"><p>{loadError}</p></IDSAlert>
      </main>
    </div>
  );
  if (!submission || !submission.form)
    return <p className="ids-mt-4">Inlämningen saknar formulärdata.</p>;

  const { userId, status, submittedAt, reviewedBy, reviewedAt, reviewComment, form } = submission;
  const { formTitle, version, description, sections = [] } = form;

  const currentIdentifier = user?.id;
  const assignedToCurrent = Array.isArray(reviewedBy) && currentIdentifier && reviewedBy.includes(currentIdentifier);
  const currentAuthorIdentifier = user?.id || user?.sub || user?.email;
  const submissionUserProfile = getUserProfileDetails(userId, userProfilesById);
  const submissionUserDisplayName =
    submissionUserProfile?.name || resolveE2EUserDisplayName(userId, userProfilesById);
  const submissionUserEmail = submissionUserProfile?.email || "—";
  const submissionUserOrganization = submissionUserProfile?.organizationName || "—";

  const authorName = currentAuthorIdentifier
    ? resolveE2EUserDisplayName(currentAuthorIdentifier, userProfilesById)
    : (user?.email || "Du");

  const assignedReviewerIds = Array.isArray(reviewedBy) ? reviewedBy : [];
  // Members added by an administrator have no E2E profile until their first
  // login, so fall back through membership details before showing the raw id.
  const colleagueLabel = (colleague) =>
    colleague.name
    || colleague.email
    || resolveE2EUserDisplayName(colleague.userId, userProfilesById);

  const reviewerLabel = (reviewerId) => {
    const colleague = assignableReviewers.find((r) => r.userId === reviewerId);
    return colleague
      ? colleagueLabel(colleague)
      : resolveE2EUserDisplayName(reviewerId, userProfilesById);
  };

  // Colleagues not already assigned, current user excluded since they have
  // their own "Tilldela mig" button.
  const selectableReviewers = assignableReviewers.filter(
    (r) => r.userId !== currentIdentifier && !assignedReviewerIds.includes(r.userId)
  );

  return (
    <div className="ids-m-6">
      <main className="ids-content ids-p-6" style={{ maxWidth: "1400px", margin: "2rem auto" }}>
        <div className="ids-mb-6">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '1rem'}}>
            <div>
              <IDSBreadcrumbs lead="Du är här:">
                <Link to="/start">Start</Link>
                <Link to="/e2e/reviewer/submissions">E2E Granskarportal</Link>
                <a>Granska formulär</a>
              </IDSBreadcrumbs>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.75rem', flexWrap: 'wrap' }}>
              {selectableReviewers.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem' }}>
                  <IDSSelect
                    label="Tilldela kollega"
                    value={selectedReviewerId}
                    onChange={(e) => setSelectedReviewerId(e.target.value)}
                  >
                    <option value="">Välj granskare</option>
                    {selectableReviewers.map((reviewer) => (
                      <option key={reviewer.userId} value={reviewer.userId}>
                        {colleagueLabel(reviewer)}
                      </option>
                    ))}
                  </IDSSelect>
                  <IDSButton
                    secondary
                    size="m"
                    onClick={handleAssignToColleague}
                    disabled={assignLoading || !selectedReviewerId}
                  >
                    Tilldela
                  </IDSButton>
                </div>
              )}

              {assignedToCurrent ? (
                <IDSButton secondary size="m" onClick={handleUnassign} disabled={assignLoading}>
                  {assignLoading ? 'Tar bort…' : 'Ta bort tilldelning'}
                </IDSButton>
              ) : (
                <IDSButton secondary size="m" onClick={handleAssignToMe} disabled={assignLoading}>
                  {assignLoading ? 'Tilldelar…' : 'Tilldela mig'}
                </IDSButton>
              )}
            </div>
          </div>
        </div>

        <h2 className="ids-heading-l ids-mb-6">Granska formulär</h2>

        <div className="ids-mb-6">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
            <div>
              <p className="ids-mb-2"><strong>Formulär:</strong> {formTitle} v{version}</p>
              {description && <p className="ids-mb-2">{description}</p>}
              {submittedAt && (
                <p className="ids-mb-2">
                  <strong>Inskickad:</strong>{' '}
                  {formatDateTimeNoSeconds(submittedAt)}
                </p>
              )}
              <p className="ids-mb-2"><strong>Status:</strong> {getSubmissionStatusLabel(status)}</p>

            </div>

            <div>
              <p className="ids-mb-2"><strong>Användare:</strong> {submissionUserDisplayName}</p>
              <p className="ids-mb-2"><strong>Organisation:</strong> {submissionUserOrganization}</p>
              <p className="ids-mb-2"><strong>Mejladress:</strong> {submissionUserEmail}</p>

              <div className="ids-mb-2">
                <strong>Granskare:</strong>{' '}
                {assignedReviewerIds.length === 0 && <span>Ingen tilldelad</span>}
                {assignedReviewerIds.length > 0 && (
                  <span style={{ display: 'inline-flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {assignedReviewerIds.map((reviewerId) => (
                      <span
                        key={reviewerId}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.35rem',
                          background: 'var(--ids-palette-neutral-95)',
                          borderRadius: '999px',
                          padding: '0.15rem 0.6rem',
                          fontSize: '0.9rem',
                        }}
                      >
                        {reviewerLabel(reviewerId)}
                        {reviewerId !== currentIdentifier && (
                          <button
                            type="button"
                            aria-label={`Ta bort ${reviewerLabel(reviewerId)} från granskningen`}
                            disabled={assignLoading}
                            onClick={() => handleUnassignReviewer(reviewerId)}
                            style={{
                              border: 'none',
                              background: 'none',
                              cursor: 'pointer',
                              fontSize: '1rem',
                              lineHeight: 1,
                              padding: 0,
                            }}
                          >
                            ×
                          </button>
                        )}
                      </span>
                    ))}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <hr className="ids-mb-6" />

        <div style={{ display: "flex", gap: "1.5rem", alignItems: "flex-start" }}>
          <div style={{ flex: "1 1 auto", minWidth: 0 }}>
            <section style={{ marginTop: "1rem" }}>
              <h2 className="ids-heading-m">Svar</h2>
              <div className="ids-mt-2 ids-mb-4" style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", alignItems: "center" }}>
                <IDSCheckbox
                  checked={showOnlyWithComments}
                  onChange={() => setShowOnlyWithComments((v) => !v)}
                >
                  Visa endast frågor med kommentarer
                </IDSCheckbox>
              </div>

              {sections.length === 0 && <p><em>Inga sektioner hittades.</em></p>}
              {sections.length > 0 && visibleSections.length === 0 && (
                <p><em>Inga frågor med kommentarer.</em></p>
              )}

              <SubmissionSectionTreeWithComments
                sections={visibleSections}
                submissionId={id}
                submissionUserId={submission?.userId}
                authorName={authorName}
                allComments={allComments}
                onCommentAdded={refreshComments}
              />
            </section>
          </div>

          <SectionNavigator sectionStatus={sectionStatus} />
        </div>

        <AttachmentListSection
          attachments={attachments}
          onDownloadAttachment={(att) => downloadAttachment(id, att.id, true)}
        />

        <hr className="ids-mb-6" />

        <div className="ids-mb-6 ids-ml-2 ids-mt-2">
          <IDSTextarea
            label="Kommentarer"
            block
            rows={4}
            value={comment}
            onChange={(e) => { setComment(e.target.value); setCommentError(""); }}
            style={{ width: "100%" }}
          />
          {commentError && (
            <div className="ids-mt-2">
              <IDSAlert type="error"><p>{commentError}</p></IDSAlert>
            </div>
          )}
          {actionError && (
            <div className="ids-mt-2">
              <IDSAlert type="error"><p>{actionError}</p></IDSAlert>
            </div>
          )}
        </div>

        <div className="ids-mb-6" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <IDSButton size="m" onClick={() => handleDecision('approve')}>
            Godkänn
          </IDSButton>

          <IDSButton secondary size="m" onClick={() => handleDecision('reject')}>
            Avslå
          </IDSButton>

          <IDSButton secondary size="m" onClick={handleRequestClarification} disabled={clarifyLoading}>
            {clarifyLoading ? 'Begär…' : 'Begär förtydligande'}
          </IDSButton>

          {clarifyError && (
            <span style={{ color: 'var(--ids-palette-error-40)', fontSize: '0.875rem' }}>
              {clarifyError}
            </span>
          )}
        </div>
      </main>
    </div>
  );
}